import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { GeneralHelper, ToasterHelper } from '@shared/helpers';
import { CurrentUserService } from '@core/services';
import { Router } from '@angular/router';
import { AppRoutes } from '@shared/enums';
import { BrowserStorageHelper } from '@core/helpers';
import { environment } from '@environments/environment';
import { CookieHelper } from '@core/helpers/cookie.helper';

export const authenticationInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    const messageService = inject(MessageService);
    const currentUserService = inject(CurrentUserService);
    const router = inject(Router);

    let userServiceImpersonation = currentUserService.getImpersonatedUser();

    const modifiedReq = req.clone({
        headers: userServiceImpersonation
            ? req.headers
                  .set('X-Impersonation-UserId', `${userServiceImpersonation.id}`)
                  .set('Cache-Control', `no-cache`)
                  .set('X-User-ID', `${userServiceImpersonation.mainUserId}`)
            : req.headers
                  .set('Cache-Control', `no-cache`)
                  .set('X-User-ID', `${currentUserService.getCurrentUser().id}`),
    });

    return next(modifiedReq).pipe(
        tap(response => {
            if (response instanceof HttpResponse && req.url.startsWith(environment.apiUrl)) {
                const responseUserId = response.headers.get('x-user-id');

                let currentUserId = currentUserService.getCurrentUser()?.id || '';
                if (BrowserStorageHelper.getImpersonatedUser() !== null) {
                    currentUserId = BrowserStorageHelper.getImpersonatedUser()?.mainUserId || '';
                }
                if (responseUserId && currentUserId && responseUserId !== currentUserId.toString()) {
                    window.location.reload();
                }
            }
        }),
        catchError(response => {
            if (response?.status === 412) {
                window.location.href = `${environment.accountsUrl}register/quick-order?redirectUri=${GeneralHelper.getAppUrl()}`;
                return throwError(response);
            }

            if (response?.url?.toLowerCase().indexOf('api/order-results-shareable') === -1) {
                if (response?.status === 403 || response?.status === 401) {
                    router.navigate([AppRoutes.UNAUTHORIZED]);
                    BrowserStorageHelper.removeImpersonatedUser();
                    CookieHelper.deleteImpersonationCookie();
                    currentUserService.setUserToImpersonate(null);
                    return throwError(response);
                }
            }

            if (
                response?.url?.toLowerCase().indexOf('api/users/get-user-preferences') > -1 ||
                response?.url?.toLowerCase().indexOf('api/autosuggest') > -1 ||
                response?.url?.toLowerCase().indexOf('api/payment-methods/edit-payment-method-active-status') > -1 ||
                response?.url?.toLowerCase().indexOf('api/order-setup/import-order') > -1
            ) {
                return throwError(response);
            }

            if (response?.error?.error === 'NeedUserConfirmationException') {
                return throwError(response);
            }

            if (response?.status === 500) {
                messageService.add(ToasterHelper.error());
                return throwError(response);
            }

            if (response?.status === 400 && response?.url?.toLowerCase().indexOf('api/sifs/upload-sif-file') > -1) {
                return throwError(response);
            }

            const backendError = response?.error?.message || response?.error?.Message || '';

            if (response?.status === 400) {
                messageService.add(
                    ToasterHelper.error(
                        backendError || "We're having trouble processing this request. Please try again soon."
                    )
                );
                return throwError(response);
            }

            messageService.add(
                ToasterHelper.error(backendError || "Something didn't quite go as planned. Please try again later.")
            );
            return throwError(response);
        })
    );
};
